import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import axios from "axios";
import { toast } from "../utils/toast";
import { useSubmitGuard } from "../utils/useSubmitGuard";
import { errorMessage } from "../utils/errorMessage";
import { API_DETAILS, COOKIE_KEYS } from "../App";
import "../styles/PricingPage.css";
import "../styles/Home.css";
import BrandLogo from "./BrandLogo";

const PricingPage = () => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(""); // ✅ track selected plan
  const [apiPlans, setApiPlans] = useState([]);
  // "loading" until the plans arrive. The cards have no prices of their own any
  // more, so there is nothing honest to show before then.
  const [plansState, setPlansState] = useState("loading");
  const [busy, setBusy] = useState(false);
  // `busy` stays true through the redirect to Stripe, so it drives the button;
  // the guard is the lock against two clicks in the same frame.
  const [, guard] = useSubmitGuard();

  // Plan ids live in the database alongside the Stripe billing details. Reading
  // them here is what lets a returning patient go straight from this page to
  // payment, instead of being sent to their profile to pick the plan again.
  useEffect(() => {
    axios
      .get(API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + "/subscription-plans")
      .then((res) => {
        setApiPlans(res.data || []);
        setPlansState("loaded");
      })
      .catch(() => {
        setApiPlans([]);
        setPlansState("failed");
      });
  }, []);

  // An existing patient whose membership lapsed already has an account, so the
  // sign-up flow is a dead end for them. This mirrors the criterion the patient
  // route guard already uses, read-only — no auth behaviour is changed here.
  // Matching userType too keeps providers and admins on the sign-up path rather
  // than sending them to a route that would bounce them straight back out.
  const userId = Cookies.get(COOKIE_KEYS.userId);
  const hasExistingSession =
    Boolean(Cookies.get(COOKIE_KEYS.userId)) &&
    Cookies.get(COOKIE_KEYS.userType) === "PATIENT" &&
    Boolean(userId);

  // What each plan is called and how it is pitched. Only the words live here:
  // every number on a card — price, appointments, diagnoses, how long chat
  // lasts — comes from /subscription-plans, which reads the same rows checkout
  // charges from. These cards used to carry prices of their own, and CLAUDE.md
  // forbids exactly that because they once disagreed with what Stripe billed.
  const PLAN_COPY = {
    STANDARD: { name: "Youro One", badge: "" },
    PREMIUM: { name: "Youro Plus", badge: "Most Popular" },
    ONETIME: { name: "Youro On Demand", badge: "" },
  };
  const PLAN_ORDER = ["STANDARD", "PREMIUM", "ONETIME"];

  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

  const featuresFor = (api) => {
    const perYear = api.recurring && api.durationDays >= 360 && api.durationDays <= 366;
    const features = [];
    if (api.diagnosisLimit != null) {
      features.push(api.diagnosisLimit > 1
        ? `Up to ${api.diagnosisLimit} diagnoses treated`
        : plural(api.diagnosisLimit, "diagnosis treated", "diagnoses treated"));
    }
    if (api.numAppointments != null) {
      features.push(`${plural(api.numAppointments, "video appointment", "video appointments")} with your urology-trained provider${perYear ? " each year" : ""}`);
    }
    if (api.recurring) {
      features.push("Symptom Score calculation before each appointment");
    }
    features.push(api.chatDurationDays && !api.recurring
      ? `Direct messaging with your provider for ${plural(api.chatDurationDays, "day", "days")}`
      : "Direct messaging with your provider");
    if (api.recurring) {
      features.push("Prescription refills");
    }
    if (api.educationAccess) {
      features.push("Curated educational content");
    }
    return features;
  };

  const plans = PLAN_ORDER
    .map((key) => {
      const api = apiPlans.find((p) => p.name === key);
      if (!api) return null;
      return {
        ...PLAN_COPY[key],
        apiName: key,
        price: `$${api.price}`,
        period: api.recurring ? (api.durationDays >= 360 && api.durationDays <= 366 ? "/year" : `/${api.durationDays} days`) : "once",
        features: featuresFor(api),
      };
    })
    .filter(Boolean);

  const isCTAEnabled = !!selectedPlan;

  /**
   * A signed-in patient goes straight from here to Stripe. Sending them to their
   * profile instead meant re-picking the plan they had just chosen before any
   * payment button appeared — the same choice made twice.
   *
   * Someone without an account still has to register first, since a checkout has
   * to be attached to a patient.
   */
  const renewOrSignUp = guard(async () => {
    if (!isCTAEnabled || busy) return;

    if (!hasExistingSession) {
      navigate("/signup", { state: { selectedPlan } });
      return;
    }

    const chosen = plans.find((p) => p.name === selectedPlan);
    const match = apiPlans.find((p) => p.name === chosen?.apiName);
    if (!match) {
      toast.error("We couldn't load that plan. Refresh the page to try again.");
      return;
    }

    setBusy(true);
    try {
      const res = await axios.post(
        API_DETAILS.baseUrl +
          API_DETAILS.PORT +
          API_DETAILS.baseExtension +
          `/payment/create-checkout-session?userId=${encodeURIComponent(userId)}&planId=${encodeURIComponent(match.planId)}`
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("We couldn't open the payment page. Try again in a moment.");
        setBusy(false);
      }
    } catch (err) {
      // The server refuses plans the patient isn't eligible for — for instance a
      // one-time pass when their membership is still running — and its message
      // explains why, so show that rather than a generic failure.
      toast.error(
          errorMessage(err, "We couldn't start checkout. Try again in a moment.")
      );
      setBusy(false);
    }
  });

  return (
    <div className="pricing-page">
      <header className="home-nav">
        <Link to="/" className="home-nav-left" aria-label="Youro home">
          <BrandLogo />
        </Link>
        <nav className="home-nav-center">
          <button
            className="home-nav-link"
            onClick={() => navigate("/about")}
          >
            About
          </button>
          <button
            className="home-nav-link"
            onClick={() => navigate("/conditions")}
          >
            Find Your Diagnosis
          </button>
          <button
            className="home-nav-link"
            onClick={() => navigate("/assessment")}
          >
            Symptom Assessment
          </button>
        </nav>
        <div className="home-nav-right">
          <button
            className="home-nav-signin"
            onClick={() => navigate("/login")}
          >
            Sign In
          </button>
          <button
            className="home-nav-get-started"
            onClick={() => navigate("/signup")}
          >
            Get Started
          </button>
        </div>
      </header>

      <div className="pricing-content">
        <button className="pricing-back" onClick={() => navigate(-1)}>
          ← Back
        </button>

        <h1 className="pricing-title">Choose Your Care Plan</h1>

        <p className="pricing-subtitle">
          Select the membership that best fits your urologic health needs. All plans include access
          to our expert urology-trained providers.
        </p>

        {plansState === "loading" && (
          <p className="pricing-status" role="status">Loading plans…</p>
        )}
        {plansState !== "loading" && plans.length === 0 && (
          <p className="pricing-status" role="alert">
            We couldn't load our plans just now. Refresh the page to try again.
          </p>
        )}

        <div className="pricing-grid">
          {plans.map((p) => {
            const isSelected = selectedPlan === p.name;

            return (
              <div
                key={p.name}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                className={[
                  "pricing-card",
                  p.badge ? "popular" : "",
                  isSelected ? "selected" : "",
                ].join(" ")}
                onClick={() => setSelectedPlan(p.name)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setSelectedPlan(p.name);
                }}
              >
                {p.badge ? <div className="pricing-badge">{p.badge}</div> : null}

                <h2 className="plan-name">{p.name}</h2>

                <div className="plan-price">
                  <span className="price">{p.price}</span>
                  <span className="period">{p.period}</span>
                </div>

                <ul className="plan-features">
                  {p.features.map((f) => (
                    <li key={f} className="feature">
                      <span className="check">✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="pricing-footer">
          <button
            className="pricing-cta"
            disabled={!isCTAEnabled || busy}
            onClick={renewOrSignUp}
          >
            {busy
              ? "Opening secure payment…"
              : hasExistingSession
              ? "Continue to Payment"
              : "Continue to Sign Up"}{" "}
            <span className="arrow">➜</span>
          </button>

          {/* ✅ sentence below CTA */}
          <p className="pricing-footer-note">
            All plans include secure messaging, prescription management, and access to urology-trained providers.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
